import { useState, type JSX } from 'react'
import { ChevronDown, Globe } from 'lucide-react'

import { AUTO_TIMEZONE, TIMEZONE_OPTIONS, timezoneOffsetLabel } from '../../utils/timezone.js'
import { useTimezone } from '../../hooks/useTimezone.js'
import { cn } from '../../utils/cn.js'

/**
 * Timezone picker rendered inside a header profile dropdown. Every timestamp
 * in the app is formatted through `utils/timezone.ts`, so changing the
 * selection here re-renders them in place — no reload.
 */
export default function TimezoneMenuItem(): JSX.Element {
  const [open, setOpen] = useState(false)
  const { timezone: resolved, stored, setTimezone } = useTimezone()

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] text-gray-600 transition-colors hover:bg-gray-50"
      >
        <Globe className="h-4 w-4 text-gray-400" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[10px] text-gray-400">Timezone · {timezoneOffsetLabel(resolved)}</span>
          <span className="truncate text-[13px] font-medium text-gray-800">
            {stored === AUTO_TIMEZONE ? resolved : stored}
          </span>
        </div>
        <ChevronDown className={cn('h-3 w-3 text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 max-h-56 w-60 overflow-y-auto rounded-lg border border-gray-100 bg-white py-1 shadow-xl">
          {TIMEZONE_OPTIONS.map((tz) => (
            <button
              key={tz.value}
              onClick={() => { setTimezone(tz.value); setOpen(false) }}
              className={cn(
                'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] transition-colors',
                stored === tz.value ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              <span className="truncate">{tz.label}</span>
              <span className="shrink-0 text-[10px] text-gray-400">
                {timezoneOffsetLabel(tz.value === AUTO_TIMEZONE ? resolved : tz.value)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
